import {
  InputField,
  Typography,
  InlineMessage,
  Icon,
} from "@saviynt/design-system";
import { FormattedMessage, useIntl } from "react-intl";
import {
  ALPHA_NUM_REGEX,
  POLICY_DESCRIPTION_MAX_LENGTH,
  POLICY_NAME_MAX_LENGTH,
} from "../../../constants/policies.constants";
import { usePoliciesForm } from "../PoliciesFormContext";
import { useState } from "react";

type NameError = "max" | "regex" | null;

export default function PoliciesNameDescription() {
  const intl = useIntl();
  const {
    name: policyName,
    setName: setPolicyName,
    description: policyDescription,
    setDescription: setPolicyDescription,
  } = usePoliciesForm();
  const [nameError, setNameError] = useState<NameError>(null);
  const [descError, setDescError] = useState<NameError>(null);

  const handleInputBlur =
    (maxLength: number, setError: (e: NameError) => void) =>
    (e: React.FocusEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (!val) setError(null);
      else if (val.length > maxLength) setError("max");
      else if (!ALPHA_NUM_REGEX.test(val)) setError("regex");
      else setError(null);
    };

  return (
    <div className="grid grid-cols-2 gap-4 mb-6">
      <div>
        <InputField
          name="policyName"
          placeholder={intl.formatMessage({
            defaultMessage: "e.g. Terminated Users with Active NHI",
            id: "saiRVK",
            description: "Placeholder for policy name input",
          })}
          label={
            <Typography kind="label" htmlFor="policyName">
              <FormattedMessage
                defaultMessage="Name"
                id="PldmPL"
                description="Label for policy name input"
              />
            </Typography>
          }
          value={policyName}
          setValue={setPolicyName}
          isRequired
          onBlur={handleInputBlur(POLICY_NAME_MAX_LENGTH, setNameError)}
          isCritical={!!nameError}
          setIsCritical={(val: boolean) => !val && setNameError(null)}
          CriticalHelperText={
            nameError && (
              <InlineMessage
                text={
                  nameError === "max"
                    ? `Maximum ${POLICY_NAME_MAX_LENGTH} characters allowed.`
                    : "Only letters (A-Z) and numbers (0-9) are allowed"
                }
                colorTheme="critical"
                size="small"
                leftIcon={<Icon kind="AlertCriticalFilled" />}
              />
            )
          }
        />
      </div>
      <div>
        <InputField
          name="policyDescription"
          placeholder={intl.formatMessage({
            defaultMessage: "What risk does this policy detect?",
            id: "1Jk2oq",
            description: "Placeholder for policy description input",
          })}
          label={
            <Typography kind="label" htmlFor="policyDescription">
              <FormattedMessage
                defaultMessage="Description"
                id="OjRI38"
                description="Label for policy description input"
              />
            </Typography>
          }
          value={policyDescription}
          setValue={setPolicyDescription}
          onBlur={handleInputBlur(POLICY_DESCRIPTION_MAX_LENGTH, setDescError)}
          isCritical={!!descError}
          setIsCritical={(val: boolean) => !val && setDescError(null)}
          CriticalHelperText={
            descError && (
              <InlineMessage
                text={
                  descError === "max"
                    ? "Maximum 300 characters allowed."
                    : "Only letters (A-Z) and numbers (0-9) are allowed"
                }
                colorTheme="critical"
                size="small"
                leftIcon={<Icon kind="AlertCriticalFilled" />}
              />
            )
          }
        />
      </div>
    </div>
  );
}
